import { CodebookInterface, MetaDataInterface } from 'models';
import { ResourceContextAction } from 'models/contextActions';
import { ProductionPlanningDeliveryPoint } from 'models/dispatcher';
import {
  CountryCode,
  DiagramPeriodApprovalStatus,
  DiagramRevisionPropagationStatus,
  PlanRevisionStatus,
  TimeSeriesType
} from 'utils/constants';
import { DeliveryPointInterface } from '../eai/eaiTypes';
import { ReservedCapacityGuardingAction } from './dispatcherReservedCapacityGuardingTypes';
import { AlarmDto } from 'views/modules/System/Alarms/alarmsModels';

// ==============================|| DISPATCHER TYPES ||============================== //

export type Tag = {
  name: string;
};

export type EntityDimensionType = {
  id: string;
  name: string;
};

export type DispatcherStateType = {
  alarms: AlarmDto[];
  codebooks: CodebookInterface[];
  marketHours: MarketPeriodInterface[];
  marketPeriods: MarketPeriodInterface[];
  selectedBusinessDay: string;
  contextActions: ResourceContextAction[];
  deviceProductionPlanListForOnlineDispatching: GetDeviceProductionPlanDetailResponse[];
  deviceProductionPlanListForPlanning: GetDeviceProductionPlanDetailResponse[];
  sseToken?: string;
  operationCalendar: {
    calendarList: OperationCalendarInterface[] | undefined;
    events: CalendarEventProps[] | undefined;
    eventDetail: OperationEventDetailInterface | undefined;
    activityEvents: ActivityEvent[] | undefined;
  };
  etag: DispatcherEtagInterface;
  metricSpecs: MetricSpecInterface[];
  balancingGroup: {
    deliveryPointList: DeliveryPointInterface[];
  };
  timeSeries: TimeSeriesQueryInterface | undefined;
  timeSeriesListForOnlineDispatching: TimeSeriesInterface[];
  timeSeriesListForPlanning: {
    SYSTEM: TimeSeriesInterface[];
    BALANCING_GROUP: TimeSeriesInterface[];
    DELIVERY_POINT: TimeSeriesInterface[];
    DEVICE: TimeSeriesInterface[];
  };
  timeSeriesListForHx: {
    BALANCING_GROUP: TimeSeriesInterface[];
    DEVICE: TimeSeriesInterface[];
    SYSTEM: TimeSeriesInterface[];
  };
  timeSeriesListForReservedCapacityGuarding: {
    DELIVERY_POINT: TimeSeriesInterface[];
    DEVICE: TimeSeriesInterface[];
  };
  dailyDiagram: DailyDiagramInterface | undefined;
  dailyDiagramList: DailyDiagramListItemInterface[];
  productionPlanActivityLog: ActivityLogInterface[];
  dailyDiagramActivityLog: ActivityLogInterface[];
  calendarActivityLog: ActivityLogInterface[];
  activityLogs: ActivityLogInterface[];
  isReady: IsDispatcherReadyInterface;
  reservedCapacityGuardingActions: ReservedCapacityGuardingAction[];
  isReservedCapacityGuardingActionsReady: boolean;
};

export interface IsDispatcherReadyInterface {
  isDailyDiagramReady: boolean;
  isProductionPlanListReady: boolean;
  isDeviceMetricListReady: boolean;
  isMetricListForBalanceGroupChartReady: boolean;
  isMetricListForSystemValuesChartReady: boolean;
}

export interface DispatcherEtagInterface {
  operationEvent?: string;
}

export interface CreateOperationEventInterface {
  name: string;
  description: string;
  eventTypeCode: string;
  eventStatus: string;
  serviceCode?: string;
  operationEventPayloadType: string;
  startDateTime: string;
  endDateTime: string;

  // Device production availability payload
  availablePower: string;
  electricalPowerUnit: string;
  // Device production availability payload
}

export interface CreateOperationEventForContractInterface {
  name: string;
  description: string;
  eventTypeCode: string;
  eventStatus: string;
  serviceCode?: string;
  operationEventPayloadType: string;
  startDateTime: string;
  endDateTime: string;

  // Contract fulfillment payload
  deliveryContractId: string;
  // Contract fulfillment payload
}

export enum OperationEventStatus {
  SCHEDULED = 'SCHEDULED',
  CANCELED = 'CANCELED'
}

export enum OperationEventPayloadType {
  DEVICE_PRODUCTION_AVAILABILITY = 'DEVICE_PRODUCTION_AVAILABILITY',
  CONTRACT_FULFILLMENT = 'CONTRACT_FULFILLMENT'
}

export enum OperationEventTypeCode {
  SHUTDOWN = 'SHUTDOWN',
  MAINTENANCE = 'MAINTENANCE',
  MALFUNCTION = 'MALFUNCTION',
  CONTRACT = 'CONTRACT'
}

export interface OperationEventDetailInterface {
  id: string;
  operationCalendarId: string;
  name: string;
  type: string;
  deviceId: string;
  availablePower: string;
  unit: string;
  deliveryContractId: string;
  startDateTime: string;
  endDateTime: string;
  description: string;
  meta: MetaDataInterface;
}

export interface OperationCalendarInterface {
  operationCalendarId: string;
  name: string;
  deviceId: string;
  operationEvents: OperationEventInterface[];
}

export interface OperationEventInterface {
  operationEventId: string;
  validFrom: string;
  validTo: string;
  name: string;
  description: string;
  operationEventTypeCode: string;
  operationEventStatus: string;
  serviceCode: string;
  payload: string;
  startDateTime: string;
  endDateTim: string;
}

export type CalendarEventProps = {
  id: string;
  deviceOffline: boolean;
  color: string;
  textColor?: string;
  description: string;
  startDateTime: Date;
  endDateTime: Date;
  name: string;
  type: OperationEventTypeCode;
  availablePower: string;
  deviceId: string;
  deliveryContractId: string;
  status: OperationEventStatus;
};

export type ActivityEvent = {
  id: string;
  creator: string;
  createdDateTime: string;
  logLevel: string;
  code: string;
  relatedEntity: string;
  tags?: Tag[];
  parameters?: ActivityEventParameters[];
  device?: EntityDimensionType;
  emsParty?: EntityDimensionType;
  deliveryPoint?: EntityDimensionType;
  balancingGroup?: EntityDimensionType;
};

export interface MarketPeriodInterface {
  timeFrom: string;
  timeTo: string;
  startDateTime: string;
  endDateTime: string;
  timeUnitNumber: number;
}

export interface ProductionRevisionInterface {
  id: number;
  revisionNumber: number;
  closingTime: string | null;
  description: string;
  meta: MetaDataInterface;
}

export interface ProductionPlanRevisionInterface
  extends ProductionRevisionInterface {
  propagationStatus: PlanRevisionStatus;
  periods: BusinessDayPeriodInterface[];
}

export interface GetDailyDiagramListProps {
  balancingGroupId: string | bigint;
  startDate: string;
  endDate: string;
}

export interface GetTimeSeriesListParams {
  requestBody: TimeSeriesQueryInterface[];
  timeSeriesType: TimeSeriesType;
  overwriteOldDataPoints?: boolean;
  callback?: (responses: SetIntoTimeSeriesPayload[]) => void;
  ignoreMetricLabels?: boolean;
}

export interface DailyDiagramInterface {
  id: number;
  balancingGroupId: string | bigint;
  diagramDate: string;
  revisions: DailyDiagramRevisionInterface[];
  meta: MetaDataInterface;
}

export interface DailyDiagramListItemInterface {
  id: number;
  balancingGroupId: string | bigint;
  diagramDate: string;
  lastRevision: DailyDiagramListLastRevision;
  meta: MetaDataInterface;
}

export interface DailyDiagramListLastRevision {
  id: number;
  revisionNumber: number;
  propagationStatus: DiagramRevisionPropagationStatus;
  production: number;
  meta: MetaDataInterface;
}

export interface DailyDiagramRevisionInterface
  extends ProductionRevisionInterface {
  propagationStatus: DiagramRevisionPropagationStatus;
  periods: DailyDiagramPeriodInterface[];
  references: DailyDiagramRevisionReference[];
}

export interface WorkingCopyOfDailyDiagramRevisionInterface {
  id: number;
  revisionNumber: number;
  closingTime: string | null;
  description: string;
  propagationStatus?: DiagramRevisionPropagationStatus;
  periods: DailyDiagramPeriodInterface[];
  references: DailyDiagramRevisionReference[];
}

export interface DailyDiagramRevisionReference {
  productionPlanId: number | bigint | string;
  revisionNumber: number;
}

export interface BusinessDayPeriodInterface {
  periodNumber: number;
  production: number | string | null;
  unit: string;
  error?: string | undefined;
}

export interface DailyDiagramPeriodInterface
  extends BusinessDayPeriodInterface {
  approvalStatus?: DiagramPeriodApprovalStatus;
  nomination: number | string | null;
}

export interface NominationPeriodInterface {
  periodNumber: number;
  nomination: number;
}

export interface CreateDailyDiagramRequestBody {
  balancingGroupId: string | bigint;
  diagramDate: string;
  productionPlanReferences: DailyDiagramRevisionReference[];
  nominations: NominationPeriodInterface[];
}

export interface UpdateDailyDiagramProps {
  requestBody: UpdateDailyDiagramRequestBody;
  balancingGroupId: string | bigint;
  dailyDiagramId: number;
  diagramDate: string;
  callback?: () => void;
  etag?: string;
}

export interface UpdateDailyDiagramRequestBody {
  description?: string;
  productionPlanReferences: DailyDiagramRevisionReference[];
  nominations: NominationPeriodInterface[];
}

export interface AcceptDailyDiagramRequestProps {
  dailyDiagramId: number | string | bigint;
  diagramRevisionId: number;
  acceptWarnings?: boolean;
  etag?: string;
  balancingGroupId: string | bigint;
  diagramDate: string;
  callback?: () => void;
}

export interface ValidateDailyDiagramRequestProps {
  dailyDiagramId: number | string | bigint;
  diagramRevisionId: number;
  productionPlanDeliveryPoints: ProductionPlanningDeliveryPoint[];
  callback?: () => Promise<void>;
  operationInProgress?: boolean;
}

export type TargetPropagationStatus = 'CONFIRMED' | 'PROPAGATED';

export interface UpdateProductionPlanProps extends ProductionPlanBaseProps {
  requestBody: CreateProductionPlanRevisionRequestBody;
  callback?: () => void;
  targetPropagationStatus?: TargetPropagationStatus;
}

export interface UpdateProductionPlanRequestBody
  extends ProductionPlanBaseProps {
  requestBody: CreateProductionPlanRevisionRequestBody;
  callback?: () => void;
}

export interface ValidationDailyDiagramItem {
  reason: string;
  relatedEntity: string;
  relatedEntityId: string;
}

export interface ValidateDailyDiagramResponseBody {
  warnings: ValidationDailyDiagramItem[];
  errors: ValidationDailyDiagramItem[];
}

export interface ActivityLogInterface {
  id: number | string | bigint;
  creator: string;
  createdDateTime: string;
  text: string;
  parameters: ActivityEventParameters[];
  code: string;
  relatedEntity: ActivityLogRelatedEntity;
}

export interface ActivityLogRelatedEntity {
  id: string;
  type: string;
}

export interface ProductionPlanBaseProps {
  deviceId: string | bigint;
  productionPlanId: string | bigint;
  selectedBusinessDay: string;
  isProductionPlansForPlanning: boolean;
  callback?: () => void;
  etag?: string;
}

export interface CopyProductionPlanProps extends ProductionPlanBaseProps {
  revisionId: number;
  requestBody: CopyProductionPlanRequestBody;
}

export interface CopyProductionPlanRequestBody {
  countryCode: CountryCode;
  productionDates: string[];
}

export interface CreateProductionPlanRequestBody {
  deviceId: string | bigint;
  countryCode: string;
  productionDates: string[];
  periodDuration: string;
  periods: BusinessDayPeriodInterface[];
}

export interface CreateProductionPlanRevisionRequestBody {
  description: string;
  periods: BusinessDayPeriodInterface[];
}

export interface GetProductionPlansBatchResponse {
  _embedded?: {
    productionPlanDetailDtoList: GetDeviceProductionPlanDetailResponse[];
  };
}

export interface GetDeviceProductionPlanDetailResponse {
  id: string | bigint;
  deviceId: string;
  productionDate: string;
  periodDuration: string;
  numberOfPeriods: number;
  revisions: ProductionPlanRevisionInterface[];
  meta: MetaDataInterface;
}

export interface GetMarketHoursResponse {
  marketHours: MarketPeriodInterface[];
  marketPeriods: MarketPeriodInterface[];
}

export interface MetricSpecInterface {
  type: string;
  displayName: string;
  description: string;
  metricKind: string;
  valueType: string;
  unitOfMeasure: string;
  monitoredResourceTypes: MonitoredResourceSpecBasicInterface[];
  labels: LabelSpecInterface[];
  metadata: MetricSpecMetadataDto;
}

export interface MonitoredResourceSpecBasicInterface {
  type: string;
  displayName: string;
  description: string;
}

export interface LabelSpecInterface {
  key: string;
  description: string;
  valueTyp: string;
}

export interface MetricSpecMetadataDto {
  samplingPeriod: string;
  ingestDelay: string;
  futureIngestionPeriod?: string;
}

export type ActivityEventParameters = {
  name: string;
  type: string;
  codebookName?: string;
  stringValue?: string;
  booleanValue?: boolean;
  dateValue?: string;
  dateTimeValue?: string;
  numberValue?: number;
  decimalValue?: number;
  codebookValue?: string;
  arrayValue?: number[] | string[];
};

export enum BESortDirection {
  ASC = 'ASC',
  DESC = 'DESC'
}

export interface TimeSeriesQueryInterface {
  metric: MetricInterface;
  monitoredResource: MonitoredResourceInterface;
  timeInterval: TimeIntervalInterface;
  seriesAlignmentFilter?: SeriesAlignmentFilterInterface;
  transformationFilter?: TransformationFilterInterface;
  pagination?: PaginationInterface;
  groupFilter?: GroupFilterInterface;
}

export interface CreateTimeSeriesQueryInterface {
  metric: MetricSpecInterface;
  monitoredResourceType: MonitoredResourceType;
  monitoredResourceLabels: LabelInterface[];
  timeInterval: TimeIntervalInterface;
  metricLabels?: LabelInterface[];
  transformationFilter?: TransformationFilterInterface;
  seriesAlignmentFilter?: SeriesAlignmentFilterInterface;
  pagination?: PaginationInterface;
  groupFilter?: GroupFilterInterface;
}

export interface TimeSeriesInterface {
  metric: MetricInterface;
  valueType: string;
  unitOfMeasure: string;
  metricKind: string;
  monitoredResource: MonitoredResourceInterface;
  dataPoints?: DataPointInterface[] | undefined;
}

export interface LabelInterface {
  key: string;
  value: string | bigint;
}

export interface TimeIntervalInterface {
  startDateTime: string;
  endDateTime: string;
}

export interface SeriesAlignmentFilterInterface {
  alignmentPeriod: string;
  seriesAligner?: string;
  interpolation?: string;
  interpolationConstantValue?: TypedValueInterface;
}

export interface PaginationInterface {
  timeOrder: BESortDirection;
  pageSize: number;
}

export interface GroupFilterInterface {
  column: string;
}

export interface TypedValueInterface {
  boolValue?: string;
  int64Value?: string | number;
  doubleValue?: string | number;
  stringValue?: string;
  objectValue?: any;
}

export interface SetIntoTimeSeriesPayload {
  data?: TimeSeriesInterface;
  resource?: {
    resourceType?: MonitoredResourceType;
    resourceLabel?: MonitoredResourceTypeLabel;
  };
}

export interface SetIntoTimeSeriesPayloadBatch {
  setIntoTimeSeriesPayload?: SetIntoTimeSeriesPayload[];
  overwriteOldDataPoints?: boolean;
  ignoreMetricLabels?: boolean;
}

export enum MonitoredResourceType {
  DEVICE = 'DEVICE',
  DELIVERY_POINT = 'DELIVERY_POINT',
  BALANCING_GROUP = 'BALANCING_GROUP',
  SYSTEM = 'SYSTEM'
}

export enum MonitoredResourceTypeLabel {
  BALANCING_GROUP_ID = 'balancing_group_id',
  DELIVERY_POINT_ID = 'delivery_point_id',
  DELIVERY_POINT_ROLE = 'delivery_point_role',
  LOCAL_DISTRIBUTION_SYSTEM_ID = 'local_distribution_system_id',
  DEVICE_ID = 'device_id',
  DEVICE_ROLE = 'device_role',
  GDS_ID = 'gds_id',
  OWNING_MARKET_PARTICIPANT_ID = 'owning_market_participant_id',
  PRODUCTION_DEVICE_TYPE = 'production_device_type',
  SYSTEM = '',
  INITIAL_ENERGY_OFFTAKE = 'initial_energy_offtake',
  INITIAL_ENERGY_SUPPLY = 'initial_energy_supply'
}

export enum MetricLabelType {
  EVENT_TYPE = 'event_type',
  FVE_MODE = 'fve_mode',
  MVE_MODE = 'mve_mode',
  WATER_FLOW_TYPE = 'water_flow_type',
  PREDICTED_HORIZON = 'predicted_horizon'
}

export enum GroupFilterType {
  DELIVERY_POINT_ID = 'delivery_point_id',
  DEVICE_ID = 'device_id',
  SOURCE = 'source'
}

export interface DataPointInterface {
  interval: DateTimeIntervalInterface;
  value: TypedValueInterface;
  metadata: DataPointMetadataInterface;
}

export interface DateTimeIntervalInterface {
  dateTimeFrom: string;
  dateTimeTo: string;
}

export interface DataPointMetadataInterface {
  sourceQualityFlags: string[];
}

export interface MetricInterface {
  type: string;
  labels?: LabelInterface[];
}

export interface MonitoredResourceInterface {
  type: string;
  labels?: LabelInterface[];
}

export interface TransformationFilterInterface {
  unitOfMeasure?: string;
}

export interface DataPointMetadataInterface {
  sourceQualityFlags: string[];
}

export enum AlignmentPeriod {
  PERIOD_15_MINUTES = '900',
  PERIOD_1_MINUTE = '60'
}

export enum SeriesAligner {
  ALIGN_NONE = 'ALIGN_NONE',
  ALIGN_MIN = 'ALIGN_MIN',
  ALIGN_MAX = 'ALIGN_MAX',
  ALIGN_MEAN = 'ALIGN_MEAN',
  ALIGN_COUNT = 'ALIGN_COUNT',
  ALIGN_SUM = 'ALIGN_SUM'
}

export const INTERVAL_15_MINUTES = '900';
export const INTERPOLATION_CONSTANT_VALUE_ZERO = { doubleValue: 0 };

export enum InterpolationType {
  INTERPOLATE_NONE = 'INTERPOLATE_NONE',
  INTERPOLATE_LINEAR = 'INTERPOLATE_LINEAR',
  INTERPOLATE_SPLINE_CUBIC = 'INTERPOLATE_SPLINE_CUBIC',
  INTERPOLATE_LAST = 'INTERPOLATE_LAST',
  INTERPOLATE_CONSTANT = 'INTERPOLATE_CONSTANT'
}

export interface CreateProductionPlan {
  callback?: (newPlanId?: string | bigint) => void;
  isProductionPlansForPlanning: boolean;
  targetPropagationStatus?: TargetPropagationStatus;
  requestBody: CreateProductionPlanRequestBody;
}

export interface FetchActivityLogsParams {
  relatedEntityType?: string;
  relatedEntityIds?: string[];
  logLevels?: string[];
  createdDateTimeGt?: string;
  createdDateTimeLt?: string;
  emsPartyIds?: string[];
  balancingGroupIds?: string[];
  deviceIds?: string[];
  deliveryPointIds?: string[];
  activityEventCodes?: string[];
  tags?: string[];
  size: number;
}

export interface AlarmsResponseDto {
  _embedded?: {
    alarmDtoList: AlarmDto[];
  };
}

export interface FetchAndSetAlarmsParams {
  triggeredDateTimeFrom: string;
  triggeredDateTimeTo: string;
  deliveryPointIds?: string[];
  size?: number;
  page?: number;
}

export interface ResolveAlarmParams {
  alarmId: string;
  reason: string;
}
